import { QuoteItem } from '../../types';
import { QUOTE_EXCLUDED_LABEL, isQuoteItemExcluded, lineTotal, round2 } from '../quoteLogic';

/**
 * Reines Matching fuer das Bepreisen des Original-LV: aus den Textkoordinaten
 * des Originals (pdfjs-Extraktion) und den Angebotspositionen werden
 * Stamp-Anweisungen fuer die EP-/GB-Spalten berechnet. Bewusst ohne jede
 * PDF-Abhaengigkeit gehalten — es geht um Geldbetraege, die Logik muss ohne
 * Browser/Worker deterministisch testbar sein.
 *
 * Zwei Strategien, in dieser Reihenfolge:
 *  1. 'position' — Zeile ueber die LV-Positionsnummer (exakter Text-Treffer).
 *     Greift nur, wenn JEDE Angebotsposition eine Nummer hat und jede im
 *     Original gefunden wird.
 *  2. 'order' — Preiszeilen-Anker: eine Preiszeile ist eine Textzeile mit je
 *     einem €-Feldmarker in der EP- UND in der GB-Spalte. Diese Zeilen werden
 *     in Dokumentreihenfolge auf die Positionen gelegt. Notwendig fuer LVs
 *     ohne Positionsnummern (Pos.Nr.-Spalte leer). Sicherung: die Anzahl muss
 *     exakt passen, und wo die Zeile eine Menge ausweist, muss sie zur
 *     Angebotsmenge passen.
 *
 * Was nicht eindeutig zugeordnet werden kann, landet in `unmatched` — es wird
 * nie geraten und nie teilweise gestempelt.
 *
 * WICHTIG: Beide Strategien erwarten die VOLLSTAENDIGE Positionsliste des
 * Angebots — auch gestrichene ('excluded') Positionen. Die Reihenfolge-
 * Strategie legt Preiszeile i auf Position i; eine vorgefilterte Liste
 * verschoebe die Zuordnung und stempelte Betraege in fremde Zeilen. Gestrichene
 * Positionen behalten also ihre Zeile und bekommen dort statt eines Betrags das
 * Wort "entfällt" (siehe priceTexts).
 */

export interface LvTextItem {
  str: string;
  // PDF-User-Space (pt, Ursprung unten links) — identisch mit dem
  // Koordinatensystem, in dem pdf-lib zeichnet.
  x: number;
  y: number;
  width: number;
  // Schriftgroesse des Textitems in pt (aus der pdfjs-Textmatrix). Optional,
  // weil sie nicht aus jedem Dokument sauber ableitbar ist.
  fontSize?: number;
}
export interface LvPageText {
  items: LvTextItem[];
}
export interface LvStamp {
  pageIndex: number; // 0-basiert
  rightX: number; // rechte Kante des Zielfelds — Betraege werden rechtsbuendig gesetzt
  y: number; // Baseline der Zeile
  text: string;
  // Schriftgroesse der Zeile im Original (pt). Fehlt sie, setzt der Stempel
  // seinen eigenen Default.
  size?: number;
}
export interface LvTotals {
  net: number;
  tax: number;
  gross: number;
}
/** Zwischensumme eines Abschnitts, fuer "Summe Titel <Titel>"-Zeilen. */
export interface LvSectionSum {
  title: string;
  subtotal: number;
  /**
   * Statt eines Betrags zu stempelnder Text — gesetzt gewinnt er ueber
   * subtotal. Einziger Fall heute: ein vollstaendig gestrichener Abschnitt
   * bekommt "entfällt" (siehe buildLvSectionSums).
   */
  text?: string;
}
/**
 * Unzuordenbare Angebotsposition in maschinenlesbarer Form — Grundlage fuer
 * die Direkt-Behebung im Editor (Nummer neu zuordnen), parallel zu den
 * Fehlertexten in `unmatched`.
 */
export interface LvUnmatchedItem {
  itemId: string;
  lvPosition?: string;
  title?: string;
}
export interface LvPriceMatch {
  stamps: LvStamp[];
  unmatched: string[];
  // Leer, wenn der Fehler nicht positionsgenau ist (z. B. Anzahl-Mismatch).
  unmatchedItems: LvUnmatchedItem[];
  strategy: 'position' | 'order';
  /**
   * Wie viele LV-Nummern im Original tatsaechlich gefunden wurden. Entscheidet,
   * ob der Reihenfolge-Fallback ueberhaupt zulaessig ist: sobald das Original
   * nachweislich nummeriert und lesbar ist, waere Stempeln nach Index geraten.
   */
  positionHits?: number;
}

/**
 * Bepreisen-Fehler mit Reparaturdaten: welche Positionen nicht zugeordnet
 * werden konnten und welche Positionsnummern das Original ueberhaupt enthaelt.
 * Lebt bewusst hier (pdfjs-frei), damit die UI die Klasse statisch importieren
 * und per instanceof erkennen kann, ohne pdfjs ins Haupt-Bundle zu ziehen.
 */
export class LvPricingError extends Error {
  readonly unmatchedItems: LvUnmatchedItem[];
  readonly candidates: string[];
  constructor(message: string, unmatchedItems: LvUnmatchedItem[] = [], candidates: string[] = []) {
    super(message);
    this.name = 'LvPricingError';
    this.unmatchedItems = unmatchedItems;
    this.candidates = candidates;
  }
}

// de-DE-Betrag mit 2 Nachkommastellen, ohne €-Zeichen (die Formularfelder
// tragen die Waehrung bereits als eigenen €-Marker).
const fmtNum = (n: number): string =>
  (Number.isFinite(n) ? n : 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * Die Texte fuer die EP- und die GB-Spalte einer Position.
 *
 * Gestrichene Positionen werden nicht bepreist, aber auch nicht uebersprungen:
 * ihre Zeile im Original-LV bleibt sichtbar und traegt statt der Betraege das
 * Wort "entfällt". Sie zaehlen dadurch weder in die gestempelten Summen (die
 * kommen aus quoteLogic) noch verschieben sie die Zeilenzuordnung.
 *
 * @param factor Gesamtrabatt-Faktor; im effektiven EP steckt zusaetzlich der
 *   Positionsrabatt, damit EP x Menge = GB aufgeht und die Summe der GBs zur
 *   Netto-Endsumme passt.
 */
const priceTexts = (item: QuoteItem, factor: number): { ep: string; gb: string } => {
  if (isQuoteItemExcluded(item)) return { ep: QUOTE_EXCLUDED_LABEL, gb: QUOTE_EXCLUDED_LABEL };
  return {
    ep: fmtNum(round2(item.unitPrice * (1 - (item.discountPercent || 0) / 100) * factor)),
    gb: fmtNum(round2(lineTotal(item) * factor)),
  };
};

// "01.010." -> "01.010" — Druckvarianten mit Punkt am Ende tolerieren.
// Exportiert, damit der Editor Nutzereingaben identisch normalisiert.
export const normalizeLvPos = (s: string): string => s.trim().replace(/\.+$/, '');
const normalizePos = normalizeLvPos;

// Spaltenkoepfe nach Entfernen von Waehrungszusaetzen ("EP €" -> "EP").
const EP_HEADER = /^(EP|E\.P\.|Einheitspreis)$/i;
const GB_HEADER = /^(GB|GP|G\.B\.|Gesamtbetrag|Gesamtpreis|Gesamt)$/i;
const stripCurrency = (s: string): string => s.replace(/€|EUR/gi, '').trim();

// Endsummen-Zeilen. Bewusst verankert, damit die Fussnote "*alle Beträge ohne
// Auszeichnung = netto" NICHT als Netto-Summenzeile durchgeht. Der
// Summen-Praefix erlaubt Formularvarianten ("Angebotssumme netto",
// "Endsumme netto"); Zwischensummen und Uebertraege sind KEINE Endsummen —
// dort stuende sonst die volle Netto-Summe an einer Teilsumme.
const NOT_A_TOTAL = /zwischensumme|übertrag|uebertrag/i;
const TOTAL_LABELS: { key: keyof LvTotals; re: RegExp }[] = [
  { key: 'net', re: /^(gesamt|[a-zäöüß-]*summe)\b[\s\S]*netto|^netto\b|^summe\s+lv\b/i },
  { key: 'tax', re: /mwst|umsatzsteuer|mehrwertsteuer/i },
  { key: 'gross', re: /^(gesamt|[a-zäöüß-]*summe)\b[\s\S]*brutto|^brutto\b/i },
];

// Zeilentoleranz: Textitems derselben Zeile weichen in y minimal ab.
const Y_TOL = 2.5;
// Wie weit darf ein €-Marker von der rechten Spaltenkante des Kopfes abweichen,
// um noch als Feld dieser Spalte zu gelten.
const COL_TOL = 36;
// Abstand des gestempelten Betrags zum €-Marker, damit nichts ueberlappt.
const EURO_GAP = 4;

const isEuro = (s: string): boolean => s.trim() === '€';

// Plausible Schriftgroessen in LV-Formularen. Ausserhalb dieses Fensters wird
// die gemessene Groesse verworfen (Skalierungs-Artefakte, Ziergrafiken).
const MIN_SIZE = 4;
const MAX_SIZE = 20;
const plausibleSize = (n?: number): number | undefined =>
  n !== undefined && n >= MIN_SIZE && n <= MAX_SIZE ? Math.round(n * 10) / 10 : undefined;

/**
 * Schriftgroesse, in der der Betrag gesetzt wird: bevorzugt die des
 * €-Feldmarkers der Zeile (das ist genau die Groesse, in der das Formular
 * seine Betraege erwartet), sonst die haeufigste Groesse der Zeile.
 */
const stampSize = (line: LvTextItem[], anchor?: LvTextItem): number | undefined => {
  const fromAnchor = plausibleSize(anchor?.fontSize);
  if (fromAnchor !== undefined) return fromAnchor;
  const sizes = line.map((t) => plausibleSize(t.fontSize)).filter((n): n is number => n !== undefined);
  if (sizes.length === 0) return undefined;
  // Modus (haeufigste Groesse) — robuster als ein Mittelwert, wenn eine Zeile
  // fette Ueberschriften und Normaltext mischt.
  const counts = new Map<number, number>();
  sizes.forEach((s) => counts.set(s, (counts.get(s) || 0) + 1));
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
};

/** Ergaenzt `size` nur, wenn eine Groesse ermittelt werden konnte. */
const withSize = (stamp: LvStamp, size?: number): LvStamp =>
  size === undefined ? stamp : { ...stamp, size };

/** Gruppiert die Textitems einer Seite zu Zeilen (oben nach unten). */
const clusterLines = (items: LvTextItem[]): LvTextItem[][] => {
  const sorted = [...items].sort((a, b) => b.y - a.y);
  const lines: LvTextItem[][] = [];
  for (const it of sorted) {
    const last = lines[lines.length - 1];
    if (last && Math.abs(last[0].y - it.y) <= Y_TOL) last.push(it);
    else lines.push([it]);
  }
  return lines.map((l) => l.sort((a, b) => a.x - b.x));
};

const lineText = (line: LvTextItem[]): string => line.map((i) => i.str).join(' ').replace(/\s+/g, ' ').trim();

/** "1.234,50" -> 1234.5; kein gueltiger Betrag -> undefined. */
const parseDe = (s: string): number | undefined => {
  const m = /^-?\d{1,3}(?:\.\d{3})*(?:,\d+)?$|^-?\d+(?:,\d+)?$/.exec(s.trim());
  if (!m) return undefined;
  const n = Number(s.trim().replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : undefined;
};

interface Columns {
  epRight?: number;
  gbRight?: number;
}

/** Spaltenkoepfe einer Seite; Folgeseiten erben die zuletzt erkannten Spalten. */
const readColumns = (items: LvTextItem[], carry: Columns): Columns => {
  const next: Columns = { ...carry };
  for (const t of items) {
    const h = stripCurrency(t.str);
    if (EP_HEADER.test(h)) next.epRight = t.x + t.width;
    else if (GB_HEADER.test(h)) next.gbRight = t.x + t.width;
  }
  return next;
};

const nearCol = (x: number, right?: number): boolean =>
  right !== undefined && Math.abs(x - right) <= COL_TOL;

// ─── Kandidaten fuer die manuelle Zuordnung ──────────────────────────────────

// Token, das wie eine LV-Positionsnummer aussieht: mehrsegmentig ("01.04.0040",
// "3.1.10"). Einzelne Zahlen fallen bewusst raus (Mengen, Seitenzahlen).
const POS_CANDIDATE = /^\d{1,4}(\.\d{1,4})+$/;
// Datumsangaben ("11.08.2026") matchen das Muster ebenfalls — aussortieren.
const LOOKS_LIKE_DATE = /^\d{1,2}\.\d{1,2}\.(19|20)\d{2}$/;

/**
 * Alle Positionsnummern-Kandidaten des Originals in Dokumentreihenfolge,
 * dedupliziert. Grundlage der Auswahl-Liste, mit der der Nutzer eine nicht
 * zuordenbare Angebotsposition im Editor auf die richtige Original-Nummer legt.
 */
export const collectLvPositionCandidates = (pages: LvPageText[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const page of pages) {
    for (const t of page.items) {
      const pos = normalizePos(t.str);
      if (!POS_CANDIDATE.test(pos) || LOOKS_LIKE_DATE.test(pos) || seen.has(pos)) continue;
      seen.add(pos);
      out.push(pos);
    }
  }
  return out;
};

// ─── Strategie 1: ueber die LV-Positionsnummer ───────────────────────────────

const matchByPosition = (pages: LvPageText[], items: QuoteItem[], factor: number): LvPriceMatch => {
  const wanted = new Map<string, QuoteItem>();
  const unmatched: string[] = [];
  const unmatchedItems: LvUnmatchedItem[] = [];
  for (const it of items) {
    const pos = normalizePos(it.lvPosition || '');
    if (!pos) {
      // Eine gestrichene Position bekommt ohnehin keinen Betrag gestempelt —
      // sie als unzuordenbar zu fuehren, liess das Bepreisen des Original-LV
      // scheitern, obwohl nichts fehlte. Einziger Ausweg waere dann das
      // Loeschen der Position, womit die Streichung als Nachweis verloren
      // ginge. Sie wird deshalb uebersprungen (nicht gefiltert: die Liste
      // bleibt fuer die Reihenfolge-Strategie vollstaendig, siehe Modulkopf).
      if (isQuoteItemExcluded(it)) continue;
      unmatched.push(it.title ? `„${it.title}“ (ohne LV-Positionsnummer)` : 'Position ohne LV-Nummer');
      unmatchedItems.push({ itemId: it.id, title: it.title });
      continue;
    }
    // Zwei Angebotspositionen mit DERSELBEN LV-Nummer: frueher gewann still
    // die erste und die zweite verschwand — ihr Preis stand nirgends im
    // bepreisten Original-LV, ohne jede Meldung. Das ist ein Geldfehler im
    // Kundendokument, also wird er gemeldet statt geschluckt.
    const first = wanted.get(pos);
    if (first) {
      unmatched.push(
        `LV-Nummer ${pos} ist im Angebot doppelt vergeben `
        + `(„${first.title || '—'}“ und „${it.title || '—'}“) — im Original gibt es dafuer nur eine Preiszeile`,
      );
      unmatchedItems.push({ itemId: it.id, lvPosition: it.lvPosition, title: it.title });
      continue;
    }
    wanted.set(pos, it);
  }

  const stamps: LvStamp[] = [];
  const matchedPos = new Set<string>();
  let cols: Columns = {};

  pages.forEach((page, pageIndex) => {
    cols = readColumns(page.items, cols);
    for (const t of page.items) {
      const pos = normalizePos(t.str);
      const item = pos ? wanted.get(pos) : undefined;
      if (!item || matchedPos.has(pos)) continue;
      if (cols.epRight === undefined && cols.gbRight === undefined) continue;
      matchedPos.add(pos);
      const { ep, gb } = priceTexts(item, factor);
      const size = plausibleSize(t.fontSize);
      if (cols.epRight !== undefined) {
        stamps.push(withSize({ pageIndex, rightX: cols.epRight, y: t.y, text: ep }, size));
      }
      if (cols.gbRight !== undefined) {
        stamps.push(withSize({ pageIndex, rightX: cols.gbRight, y: t.y, text: gb }, size));
      }
    }
  });

  for (const [pos, item] of wanted) {
    // Gestrichene Positionen wie oben: ohne Gegenstueck im Original bleibt
    // schlicht nichts zu stempeln, das ist kein Zuordnungsfehler.
    if (!matchedPos.has(pos) && !isQuoteItemExcluded(item)) {
      unmatched.push(`${pos}${item.title ? ` – ${item.title}` : ''}`);
      unmatchedItems.push({ itemId: item.id, lvPosition: pos, title: item.title });
    }
  }
  return { stamps, unmatched, unmatchedItems, strategy: 'position', positionHits: matchedPos.size };
};

// ─── Strategie 2: Preiszeilen-Anker (LV ohne Positionsnummern) ───────────────

interface PriceRow {
  pageIndex: number;
  y: number;
  epX: number; // x des €-Markers der EP-Spalte
  gbX: number;
  quantity?: number;
  size?: number; // Schriftgroesse der Zeile im Original
}

/** Alle Preiszeilen (€ in EP- UND GB-Spalte) in Dokumentreihenfolge. */
export const findPriceRows = (pages: LvPageText[]): PriceRow[] => {
  const rows: PriceRow[] = [];
  let cols: Columns = {};
  pages.forEach((page, pageIndex) => {
    cols = readColumns(page.items, cols);
    if (cols.epRight === undefined || cols.gbRight === undefined) return;
    for (const line of clusterLines(page.items)) {
      const ep = line.find((t) => isEuro(t.str) && nearCol(t.x, cols.epRight));
      const gb = line.find((t) => isEuro(t.str) && nearCol(t.x, cols.gbRight));
      if (!ep || !gb) continue;
      // Menge steht links der EP-Spalte, z. B. "1,00 psch" oder "6,00 St".
      // Von rechts nach links suchen: die Menge steht der EP-Spalte am
      // naechsten, eine etwaige Pos.Nr. weiter links darf sie nicht verdraengen.
      const qtyItem = line
        .filter((t) => t.x < (cols.epRight as number) - COL_TOL)
        .reverse()
        .map((t) => parseDe(t.str))
        .find((n) => n !== undefined);
      rows.push({ pageIndex, y: ep.y, epX: ep.x, gbX: gb.x, quantity: qtyItem, size: stampSize(line, ep) });
    }
  });
  return rows;
};

/**
 * Legt Preiszeile i auf Position i. `items` MUSS die ungefilterte Angebotsliste
 * sein: die Anzahlpruefung und der Index-Zugriff verlassen sich darauf. Wird
 * hier vorgefiltert (z.B. um gestrichene Positionen zu entfernen), rutschen alle
 * Folgebetraege eine Zeile hoch — falsche Betraege im Kundendokument, ohne
 * Warnung.
 */
const matchByPriceRows = (pages: LvPageText[], items: QuoteItem[], factor: number): LvPriceMatch => {
  const rows = findPriceRows(pages);
  if (rows.length === 0) {
    return {
      stamps: [],
      unmatched: ['Im Original-LV wurden keine Preisfelder (EP/GB) gefunden'],
      unmatchedItems: [],
      strategy: 'order',
    };
  }
  if (rows.length !== items.length) {
    return {
      stamps: [],
      unmatched: [
        `Anzahl stimmt nicht: ${items.length} Angebotspositionen, aber ${rows.length} Preiszeilen im Original-LV`,
      ],
      unmatchedItems: [],
      strategy: 'order',
    };
  }

  const stamps: LvStamp[] = [];
  const unmatched: string[] = [];
  const unmatchedItems: LvUnmatchedItem[] = [];
  rows.forEach((row, i) => {
    const item = items[i];
    // Wo das Original eine Menge ausweist, muss sie zur Angebotsmenge passen —
    // sonst ist die Reihenfolge verschoben und es waere geraten.
    if (row.quantity !== undefined && Math.abs(row.quantity - (item.quantity || 0)) > 0.005) {
      unmatched.push(
        `Zeile ${i + 1} („${item.title || item.lvPosition}“): Menge ${fmtNum(item.quantity || 0)} im Angebot, ` +
          `aber ${fmtNum(row.quantity)} im Original-LV`,
      );
      unmatchedItems.push({ itemId: item.id, lvPosition: item.lvPosition, title: item.title });
      return;
    }
    const { ep, gb } = priceTexts(item, factor);
    stamps.push(
      withSize({ pageIndex: row.pageIndex, rightX: row.epX - EURO_GAP, y: row.y, text: ep }, row.size),
    );
    stamps.push(
      withSize({ pageIndex: row.pageIndex, rightX: row.gbX - EURO_GAP, y: row.y, text: gb }, row.size),
    );
  });
  return { stamps: unmatched.length ? [] : stamps, unmatched, unmatchedItems, strategy: 'order' };
};

// ─── Summenzeilen (Endsummen + "Summe Titel <Abschnitt>") ────────────────────

/**
 * Baut die `sectionSums` aus dem Ergebnis von calculateQuoteTotalsDetailed.
 *
 * Mehr als Durchreichen macht es wegen eines Falls: ein Abschnitt, dessen
 * Positionen ALLE gestrichen sind, hat keine Zwischensumme mehr und faellt
 * damit aus perSection heraus — seine Zeile "Summe Titel <Abschnitt>" im
 * Original bliebe unbestempelt, also leer. Ein leeres Summenfeld im
 * Kundendokument liest sich als vergessener Betrag; der Abschnitt bekommt
 * deshalb "entfällt" gestempelt.
 *
 * BEWUSST nicht auf reine NEP-Abschnitte ausgeweitet: die fallen wie bisher aus
 * perSection und bleiben leer. NEP heisst "angeboten, aber nicht eingerechnet"
 * — "entfällt" waere dort schlicht die falsche Aussage.
 *
 * Liegt hier und nicht in lvPriceOverlay.ts, weil dieses Modul pdfjs-frei ist
 * und damit ohne PDF-Umgebung testbar bleibt.
 */
export const buildLvSectionSums = (
  items: Pick<QuoteItem, 'sectionId' | 'excluded'>[],
  sections: { id: string; title: string }[],
  perSection: { sectionId: string | null; subtotal: number }[],
): LvSectionSum[] => {
  const titles = new Map(sections.map((s) => [s.id, s.title]));
  const sums: LvSectionSum[] = perSection
    .map((s) => ({ title: (s.sectionId && titles.get(s.sectionId)) || '', subtotal: s.subtotal }))
    .filter((s) => s.title !== '');

  // Nur ergaenzen, nie ersetzen: ein Abschnitt mit Zwischensumme ist bereits
  // drin, ein leerer Abschnitt (ohne Positionen) hat im Original nichts zu
  // suchen.
  const covered = new Set(perSection.map((s) => s.sectionId));
  for (const section of sections) {
    if (covered.has(section.id) || section.title.trim() === '') continue;
    const own = items.filter((it) => it.sectionId === section.id);
    if (own.length === 0 || !own.every(isQuoteItemExcluded)) continue;
    sums.push({ title: section.title, subtotal: 0, text: QUOTE_EXCLUDED_LABEL });
  }
  return sums;
};

const matchSums = (
  pages: LvPageText[],
  factor: number,
  totals?: LvTotals,
  sectionSums?: LvSectionSum[],
): LvStamp[] => {
  if (!totals && !sectionSums?.length) return [];
  // Abschnittstitel nach ABSTEIGENDER Laenge: die Zuordnung laeuft ueber
  // "Zeile enthaelt Titel", und ein Titel kann Praefix eines anderen sein
  // ("Nebenkosten" vs. "Nebenkosten Baustelle"). Ein `find` in Listenreihenfolge
  // liess den kuerzeren Titel die Summenzeile des laengeren kapern und stempelte
  // dort einen fremden Betrag — ein Geldfehler im Kundendokument. Ist der lange
  // Abschnitt vollstaendig gestrichen (buildLvSectionSums haengt ihn hinten mit
  // "entfällt" an), behauptete die gekaperte Zeile sogar einen Betrag fuer eine
  // Leistung, die entfaellt.
  // Bei gleich langen (also identischen) Titeln gewinnt der Eintrag MIT Betrag:
  // eine gestempelte Zwischensumme ist an einer der beiden Zeilen richtig,
  // "entfällt" waere an der aktiven Zeile in jedem Fall falsch.
  const sectionCandidates = (sectionSums ?? [])
    .map((s) => ({ sum: s, needle: s.title.trim().toLowerCase() }))
    .filter((c) => c.needle !== '')
    .sort(
      (a, b) =>
        b.needle.length - a.needle.length
        || Number(a.sum.text !== undefined) - Number(b.sum.text !== undefined),
    );
  // VERANKERTE Zeilen (mit €-Feldmarker) werden ALLE gestempelt: ein LV nennt
  // sein Netto oft zweimal — "Summe LV …" am LV-Ende UND "Gesamt netto" in
  // der Zusammenfassung; beide Felder sollen gefuellt sein. Nur fuer
  // UNverankerte Zeilen gilt weiter "letztes Vorkommen gewinnt" (die finale
  // Zusammenstellung steht hinten), und eine unverankerte Zeile kommt gar
  // nicht zum Zug, sobald es irgendwo eine verankerte gibt — sonst stiehlt
  // eine umgebrochene Fussnote ("… =" / "netto") den Netto-Stempel.
  const anchoredStamps: LvStamp[] = [];
  const anchoredKeys = new Set<string>();
  const fallback = new Map<string, LvStamp>();
  let cols: Columns = {};

  pages.forEach((page, pageIndex) => {
    cols = readColumns(page.items, cols);
    if (cols.gbRight === undefined) return;
    for (const line of clusterLines(page.items)) {
      const euroGb = line.find((t) => isEuro(t.str) && nearCol(t.x, cols.gbRight));
      const euroEp = line.find((t) => isEuro(t.str) && nearCol(t.x, cols.epRight));
      // Summenzeilen tragen nur ein Feld (GB). Zeilen mit EP UND GB sind
      // Positionszeilen und werden hier nicht angefasst.
      if (euroEp) continue;
      const text = lineText(line);
      // Auch ein €-Marker RECHTS der GB-Spalte verankert die Zeile —
      // Endsummenzeilen laufen oft ueber die volle Satzbreite hinaus.
      const euroAny = euroGb ?? line.find((t) => isEuro(t.str) && t.x > (cols.gbRight as number));
      const rightX = euroAny ? euroAny.x - EURO_GAP : (cols.gbRight as number);
      const size = stampSize(line, euroAny);
      const put = (key: string, stampText: string) => {
        const stamp = withSize({ pageIndex, rightX, y: line[0].y, text: stampText }, size);
        if (euroAny !== undefined) {
          anchoredStamps.push(stamp);
          anchoredKeys.add(key);
        } else {
          fallback.set(key, stamp);
        }
      };

      // Abschnitts-Zwischensummen zuerst: "Summe Titel X" und die
      // Zusammenfassungszeile "Titel X" nennen beide den Abschnittstitel.
      const lower = text.toLowerCase();
      const section = sectionCandidates.find((c) => lower.includes(c.needle))?.sum;
      if (section) {
        // Ein vorgegebener Text (vollstaendig gestrichener Abschnitt) gewinnt
        // ueber den Betrag: die Summenzeile im Kundendokument darf nicht leer
        // bleiben und auch keine 0,00 behaupten.
        put(
          `section:${section.title}:${pageIndex}:${Math.round(line[0].y)}`,
          section.text ?? fmtNum(round2(section.subtotal * factor)),
        );
        continue;
      }
      if (!totals) continue;
      if (NOT_A_TOTAL.test(text)) continue;
      for (const { key, re } of TOTAL_LABELS) {
        if (re.test(text)) {
          put(key, fmtNum(totals[key]));
          break;
        }
      }
    }
  });
  return [
    ...anchoredStamps,
    ...[...fallback.entries()]
      .filter(([key]) => !anchoredKeys.has(key))
      .map(([, stamp]) => stamp),
  ];
};

/**
 * Berechnet die Stamp-Anweisungen. Nicht sicher platzierbare Positionen stehen
 * in `unmatched`; der Aufrufer bricht dann ab.
 *
 * @param items VOLLSTAENDIGE Positionsliste des Angebots, inklusive gestrichener
 *   Positionen — beide Strategien ordnen ueber Nummer bzw. Index zu (siehe
 *   Modulkopf). Gestrichene Positionen werden mit "entfällt" gestempelt.
 * @param totals Endsummen OHNE gestrichene Positionen (und ohne NEP) — sie
 *   kommen aus quoteLogic und werden hier nicht nachgerechnet.
 * @param globalDiscountPercent Gesamtrabatt des Angebots. Wird anteilig in
 *   EP/GB jeder Position eingerechnet, damit die gestempelten Positionswerte
 *   zur Netto-Endsumme passen — im Original-LV gibt es keine Rabattzeile.
 * @param sectionSums Zwischensummen je Abschnitt (ohne Gesamtrabatt; der wird
 *   hier angewendet) fuer "Summe Titel <Abschnitt>"-Zeilen.
 */
export const matchLvPrices = (
  pages: LvPageText[],
  items: QuoteItem[],
  totals?: LvTotals,
  globalDiscountPercent = 0,
  sectionSums?: LvSectionSum[],
): LvPriceMatch => {
  const factor = 1 - (globalDiscountPercent || 0) / 100;

  const byPos = matchByPosition(pages, items, factor);
  let base = byPos;
  // Seit gestrichene Positionen nicht mehr als unzuordenbar gelten, kann der
  // Nummern-Anker fehlerfrei UND ohne einen einzigen Stempel enden — naemlich
  // wenn ausschliesslich gestrichene Positionen ohne Nummer uebrig sind. Ohne
  // diesen zweiten Anlass bliebe der Reihenfolge-Anker aussen vor und das
  // Original-LV kaeme voellig unbestempelt zurueck.
  const nothingStamped = items.length > 0 && byPos.stamps.length === 0;
  if (byPos.unmatched.length > 0 || nothingStamped) {
    const lvIsNumbered = items.some((it) => (it.lvPosition || '').trim() !== '');
    // Der Reihenfolge-Fallback stempelt nach Index. Das ist NUR zulaessig,
    // solange die Positionsnummern als Anker ausfallen — entweder weil das
    // Angebot keine hat oder weil im Original keine einzige wiederzufinden war.
    // Hat schon eine Nummer getroffen, ist das Original nachweislich
    // nummeriert und lesbar; ein Rest-Fehltreffer heisst dann, dass Angebot
    // und Original auseinanderlaufen. Nach Index weiterzustempeln traf in
    // diesem Fall die falschen Zeilen, sobald die Zeilenzahl zufaellig passte —
    // mit falschen Betraegen im Kundendokument und ohne jede Warnung.
    const orderAllowed = !lvIsNumbered || (byPos.positionHits ?? 0) === 0;
    if (orderAllowed) {
      const byOrder = matchByPriceRows(pages, items, factor);
      // Greift der Fallback vollstaendig, gewinnt er. Scheitern beide, wird der
      // Befund gemeldet, der zum Dokument passt: ist das LV ueberhaupt
      // nummeriert, hilft die Positionsnummern-Liste — sonst waere sie nur
      // Rauschen ("ohne LV-Positionsnummer" fuer jede Zeile) und die konkrete
      // Ursache des Preiszeilen-Ankers (Anzahl/Menge) ist die brauchbare.
      base = byOrder.unmatched.length === 0 ? byOrder : lvIsNumbered ? byPos : byOrder;
    }
  }
  if (base.unmatched.length > 0) return base;

  return {
    stamps: [...base.stamps, ...matchSums(pages, factor, totals, sectionSums)],
    unmatched: [],
    unmatchedItems: [],
    strategy: base.strategy,
  };
};
